// React
import React, { useMemo, useState, useEffect, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import ScreenReaderManager from '@cybs-ui/components/lib/screen-reader-manager';

// Internal
import styles from './component.styles';
import ListboxList from './components/listboxList';
import ListboxContainer from './components/listboxContainer';
import ListboxItem from './components/listboxItem';

const CybsListbox = React.forwardRef(
  (
    {
      children,
      customCSS,
      dataTest,
      enableListboxKeydown,
      focusedIndex,
      focusedValue,
      heightOfItem,
      i18n: i18nProp,
      level,
      menuRef,
      onKeyDown,
      onSelect,
      onScrollBottom,
      optionsTemplate,
      position,
      scrollOnLoad,
      selectedItems: selectedItemsProp,
      setSelection,
      top,
      type,
      useLowResolutionStyles,
      width,
      lang,
      id
    },
    ref
  ) => {
    const i18n = {
      ...CybsListbox.defaultProps.i18n,
      ...i18nProp
    };

    // Backward compatability fix
    // Some existing implementations have selectedItems as a string or non-array object
    // TODO: Remove this at major change.
    const selectedItems = Array.isArray(selectedItemsProp)
      ? selectedItemsProp
      : [selectedItemsProp];

    const [shouldFocusChild, focusChildNextRender] = useState(false);
    const [subDropdownChildren, setDropdownChildren] = useState(null);
    const [numberValuesRendered, setNumberValuesRendered] = useState(100);
    const [numberValuesIncrease, setNumberValuesIncrease] = useState(100);

    const handleLoadMoreItems = () => {
      setNumberValuesRendered(numberValuesRendered + numberValuesIncrease);
      setNumberValuesIncrease(numberValuesIncrease + 100);
      ScreenReaderManager.addNotification(i18n.listUpdated);
    };

    // focus the child listbox upon request
    useLayoutEffect(() => {
      if (shouldFocusChild) {
        const subListbox =
          menuRef &&
          menuRef.current &&
          menuRef.current.querySelectorAll('ul')[level + 1];
        if (subListbox) {
          subListbox.focus();
        }
        focusChildNextRender(false);
      }
    }, [shouldFocusChild]);

    // set dropdown children and focus child listbox
    function openNestedDropdown(obj) {
      setDropdownChildren(obj);
      if (enableListboxKeydown && obj) {
        focusChildNextRender(true);
      }
    }

    // Return a list of values for the selected items.
    const selectedValues = useMemo(() => {
      let values;
      if (selectedItems && selectedItems.length > 0) {
        // added existence check to avoid page crash. Keyboard behavior will be done as part of a11y
        values = selectedItems.map(item => item && item.value);
      }
      return values;
    }, [selectedItems]);

    const sanitizeValue = str => {
      // If string starts with a digit, wrap it in quotes.
      // eslint-disable-next-line
      if (str && str.indexOf('"') === -1 && str.indexOf(`'`) === -1) {
        return `"${str}"`;
      }
      return str;
    };

    // Synchronize subDropdownChildren to match selectedItems[level] (if
    //  applicable)
    const currentLevel = selectedItems?.[level];
    useEffect(() => {
      if (currentLevel?.children) {
        // this effect is triggered in the following cases:
        //  1. the user is changing their selection in this level
        //  2. the screen was resized, and the back button was just removed
        //     from the current level
        const listItem =
          menuRef &&
          menuRef.current &&
          menuRef.current.querySelector(`*[value=${sanitizeValue(currentLevel.children[0]?.value)}]`);
        const listItemTop =
          listItem &&
          listItem.getBoundingClientRect().top -
            listItem.parentNode.getBoundingClientRect().top;
        // do not use `openNestedDropdown` because it also focuses the child
        //  listbox. case (2) above would cause focus bugs on resize
        setDropdownChildren({
          value: currentLevel.value,
          children: currentLevel.children,
          level: level + 1,
          top: listItemTop <= 0 ? 1 : listItemTop
        });
      }
    }, [currentLevel, level, menuRef]);

    // Update the array of selection for the nested dropdowns.
    const handleItemClick = (event, item, selected) => {
      if (event) {
        const child = children.find(it => it.value === item.value);
        if (child && child.children) {
          const listItem =
            menuRef &&
            menuRef.current &&
            menuRef.current.querySelector(`*[value=${sanitizeValue(item.value)}]`);
          const listItemTop =
            listItem &&
            listItem.getBoundingClientRect().top -
              listItem.parentNode.getBoundingClientRect().top;
          openNestedDropdown({
            value: item.value,
            children: child.children,
            level: level + 1,
            top: listItemTop <= 0 ? 1 : listItemTop
          });
        }
        onSelect(event, item, selected);
      }
    };

    // Use handleItemClick logic as callback for keyboard input
    const handleItemKeyDown = (event, item, values) => {
      if (event) {
        const { key } = event;
        if (key === 'Enter' || key === 'Right' || key === 'ArrowRight') {
          const child = children.find(it => it.value === item.value);
          if (child && child.children) {
            const listItem =
              menuRef &&
              menuRef.current &&
              menuRef.current.querySelector(`*[value=${sanitizeValue(item.value)}]`);
            const listItemTop =
              listItem &&
              listItem.getBoundingClientRect().top -
                listItem.parentNode.getBoundingClientRect().top;
            openNestedDropdown({
              value: item.value,
              children: child.children,
              level: !level ? 1 : level + 1,
              top: listItemTop <= 0 ? 1 : listItemTop
            });
          }
        }
        if (key === 'Left') {
          openNestedDropdown(null);
        }
      }
      // Execute callback
      onKeyDown(event, item, values);
    };

    // Are there any more open listboxes past this one?
    const moreLevels =
      subDropdownChildren?.children && selectedItems.length > level + 1;

    const stylesOptions = {
      width,
      position,
      moreLevels,
      useLowResolutionStyles: useLowResolutionStyles && type === 'nested',
      ...(top && level ? { scrollTop: top, level } : { scrollTop: 0, level: 0 })
    };
    return (
      <>
        <ListboxContainer
          css={theme => [styles(theme, stylesOptions), customCSS(theme)]}
          data-test={`${dataTest}-cybsListbox`}
          focusedIndex={focusedIndex}
          focusedValue={focusedValue}
          level={level}
          onSelect={handleItemClick}
          onScrollBottom={onScrollBottom}
          onKeyDown={handleItemKeyDown}
          multiselect={type === 'multi'}
          type={type}
          selectedListItems={selectedItems}
          selectedValues={selectedValues}
          items={children}
          heightOfItem={heightOfItem}
          numberValuesRendered={numberValuesRendered}
          onLoadMoreItems={handleLoadMoreItems}
          enableListboxKeydown={enableListboxKeydown}
          optionsTemplate={optionsTemplate}
          scrollOnLoad={scrollOnLoad}
        >
          <ListboxList
            id={id}
            role={enableListboxKeydown ? 'menu' : 'listbox'}
            multiselect={type === 'multi'}
            ref={ref}
            numberOfItems={children.length}
            aria-label={i18n.label}
          >
            {children.map((item, index) => {
              let nestedProps = null;
              if (item.children?.length) {
                nestedProps = {
                  'aria-expanded': Boolean(
                    subDropdownChildren?.value &&
                      subDropdownChildren.value === item.value
                  ),
                  'aria-haspopup': true
                };
              }
              if (index < numberValuesRendered) {
                if (item.categories && item.categories.length) {
                  return (
                    <React.Fragment
                      key={`item-category-container-${item.value}`}
                    >
                      <ListboxItem
                        className="cybs-listbox-item-section"
                        dataTest={dataTest}
                        disabled
                        hasSubitems
                        i18n={i18n}
                        key={`item-category-${item.value}`}
                        label={item.label}
                        value={item.value}
                        lang={lang}
                      />
                      {item.categories.map((section, subIndex) =>
                        React.cloneElement(
                          <ListboxItem dataTest={dataTest} />,
                          {
                            'aria-selected': undefined,
                            // Per a11y team: For each category, first subItem in each category gets aria-describedby
                            // pointing to a span with text ${category name} ${localized word for "category"}
                            'aria-describedby':
                              subIndex === 0
                                ? `item-category-${item.value}`
                                : undefined,
                            disabled: section.disabled,
                            index: subIndex,
                            key: `item-subcategory-${section.value}`,
                            value: section.value,
                            templateLabel: section.label,
                            label: section.label,
                            type,
                            isSelected:
                              selectedItems &&
                              selectedItems.length > 0 &&
                              selectedValues.includes(section.value),
                            lang
                          }
                        )
                      )}
                    </React.Fragment>
                  );
                }

                if (item.value === 'no_results') {
                  return (
                    <ListboxItem
                      key={`item-${item.value}`}
                      className="cybs-listbox-item-noresults"
                      dataTest={dataTest}
                      disabled
                      value={item.value}
                      type="single"
                      lang={lang}
                      aria-labelledby={`${item.label}-description`}
                    >
                      <span id={`${item.label}-description`}>
                        {optionsTemplate
                          ? optionsTemplate.renderOption(item)
                          : item.label}
                      </span>
                    </ListboxItem>
                  );
                }
                return React.cloneElement(
                  <ListboxItem dataTest={dataTest} type={type} />,
                  {
                    ...nestedProps,
                    className: classnames(
                      'cybs-listbox-item-nested',
                      item.children?.length ? 'cybs-listbox-item-hasSubitems' : '',
                      item.hasBorders ? 'cybs-listbox-item--bordered' : '',
                      {
                        isOpen: Boolean(
                          subDropdownChildren &&
                            subDropdownChildren.children === item.value
                        )
                      }
                    ),
                    dataTest,
                    disabled: item.disabled,
                    index,
                    isSelected:
                      selectedItems &&
                      selectedItems.length > 0 &&
                      selectedValues.includes(item.value),
                    isWarning: item.isWarning,
                    itemChildren: item.children,
                    key: `item-${item.value}`,
                    label: optionsTemplate
                      ? optionsTemplate.renderOption(item)
                      : item.label,
                    templateLabel: optionsTemplate ? item : item.label,
                    type,
                    value: item.value,
                    isBack: item.isBack,
                    lang,
                    'aria-labelledby': optionsTemplate?.renderLabel
                      ? optionsTemplate.renderLabel(item)
                      : undefined
                  }
                );
              }
              return null;
            })}
          </ListboxList>
        </ListboxContainer>
        {moreLevels && (
            <CybsListbox
              dataTest={dataTest}
              onKeyDown={onKeyDown}
              onSelect={onSelect}
              position={position}
              selectedItems={selectedItems}
              setSelection={setSelection}
              type={type}
              width={width}
              top={subDropdownChildren.top}
              level={subDropdownChildren.level}
              menuRef={menuRef}
              enableListboxKeydown={enableListboxKeydown}
              optionsTemplate={optionsTemplate}
              focusedIndex={focusedIndex}
              scrollOnLoad={scrollOnLoad}
              useLowResolutionStyles={useLowResolutionStyles}
            >
              { subDropdownChildren.children }
            </CybsListbox>
          )}
      </>
    );
  }
);

CybsListbox.propTypes = {
  /**
   * id of listbox
   */
  id: PropTypes.string,

  /**
   * Styling function to allow CSS overrides via Emotion
   *
   * @ignore
   */
  customCSS: PropTypes.func,

  /**
   * dataTest string for testing.
   */
  dataTest: PropTypes.string.isRequired,

  /**
   *If `true`, key down callback is fired on the listbox itself for navigating in the list.
   */
  enableListboxKeydown: PropTypes.bool,

  /**
   * function to set the selection
   */
  setSelection: PropTypes.func,

  /**
   * value for determining how far down to push the next nested menu.
   */
  top: PropTypes.number,

  /**
   * value used to determine how many levels of nested we are currently within
   */
  level: PropTypes.number,

  /**
   * object being passed down to the component from the parent Menu
   */
  // eslint-disable-next-line react/forbid-prop-types
  menuRef: PropTypes.object,

  /**
   * lang Tag for localized listbox
   */
  lang: PropTypes.string,
  /**
   * List of items in the listbox.
   */
  children: PropTypes.oneOfType([
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
      isWarning: PropTypes.bool,
      hasBorders: PropTypes.bool
    }),
    PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string,
        label: PropTypes.string
      })
    )
  ]).isRequired,

  /**
   * Index of the currently focused item.
   */
  focusedIndex: PropTypes.number,

  /**
   * Value of the currently focused item.
   */
  focusedValue: PropTypes.string,

  /**
   * Strings for the component.
   */
  i18n: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({
      category: PropTypes.string
    })
  ]),

  /**
   * CSS height of an item element.
   */

  heightOfItem: PropTypes.number,

  /**
   * Callback fired on key down.
   */
  onKeyDown: PropTypes.func,

  /**
   * Callback fired when an item is selected.
   */
  onSelect: PropTypes.func,

  /**
   * Callback fired when scroll reaches bottom of scrollable list
   */
  onScrollBottom: PropTypes.func,

  /**
   * Determines if nested is aligned to the left or right.
   */
  position: PropTypes.shape({ horizontal: PropTypes.oneOf('left', 'right') }),

  /**
   * optionsTemplate to render multiple attributes in the listbox
   */
  optionsTemplate: PropTypes.shape({
    optionValues: PropTypes.arrayOf(
      PropTypes.shape({ value: PropTypes.string.isRequired })
    ),
    renderOption: PropTypes.func.isRequired,
    renderLabel: PropTypes.func.isRequired,
    validationTemplate: PropTypes.func.isRequired
  }),

  /**
   * Should the listbox scroll to the currently selected item on load?
   */
  scrollOnLoad: PropTypes.bool,

  /**
   * Selected item(s).
   */
  selectedItems: PropTypes.oneOfType([
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string
    }),
    PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string,
        label: PropTypes.string
      })
    )
  ]),

  /**
   * Different type of dropdowns.
   */
  type: PropTypes.oneOf(['multi', 'single', 'nested']),

  /**
   * Should the listbox use its low resolution styles. These include overlapping
   * the listboxes and hiding parent listboxes. This should be used iff
   * the children & selected items have back buttons.
   */
  useLowResolutionStyles: PropTypes.bool,

  /**
   * Width of the input and dropdown menu in px or rem.
   */
  width: PropTypes.string
};

CybsListbox.defaultProps = {
  id: undefined,
  customCSS: () => '',
  enableListboxKeydown: true,
  focusedIndex: 0,
  focusedValue: '',
  heightOfItem: 48,
  i18n: { category: 'category', listUpdated: 'list updated' },
  level: 0,
  menuRef: null,
  onKeyDown: () => {},
  lang: 'en',
  optionsTemplate: undefined,
  onSelect: () => {},
  onScrollBottom: () => {},
  position: { horizontal: 'left' },
  scrollOnLoad: true,
  selectedItems: [{}],
  setSelection: () => {},
  top: null,
  type: 'single',
  useLowResolutionStyles: false,
  width: ''
};

CybsListbox.displayName = 'CybsListbox';

export default CybsListbox;
